import React, { useState, useEffect } from 'react';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Utils
import { convertMomentToUnix, getCurrentMoment } from 'Shared/utils/DateTime.utils';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useQueryParam } from 'Shared/utils/hooks';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

// Components
import InviteTeammateButton from 'Shared/components/InviteTeammateButton/InviteTeammateButton';
import WpBackdrop from 'Wpds/components/backdrop/WpBackdrop';
import { WpPopover, WpPopoverToggle, WpPopoverContent } from 'Wpds/components/popover';
import ResendInviteButton from './ResendInviteButton/ResendInviteButton';
import { WpCard, WpCardBody } from 'Wpds/components/card';
import { Variant, WpButton, WpButtonGroup } from 'Wpds/components/button';
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';

// Styles
import './WorkspaceInviteButton.scss';

type Props = {
  inviteBtnText?: string;
  inviteBtnIcon?: string;
  btnVariant?: Variant;
  includePendingMessage?: boolean;
};

const WorkspaceInviteButton = ({ inviteBtnText, inviteBtnIcon, btnVariant, includePendingMessage = false }: Props) => {
  const { user: currentUser, getFlag, updateFlag, isPiUser } = useCurrentUser();
  const { workspace, id: workspaceId, name: workspaceName } = useWorkspace();

  const hasSeenCalendarIntegration2023InviteCallout = getFlag({
    flag: 'has_seen_calendar_integration_2023_invite_callout',
  });

  const [hasInvitedInSession, setHasInvitedInSession] = useState(false);
  const [isShowingCalendarIntegrationBlocker, setIsShowingCalendarIntegrationBlocker] = useState<boolean>(false);
  const [, setInviteModalParam] = useQueryParam(QueryParamConstants.INVITE_BY_WORKSPACE_MODAL);

  const isTeammateItem = !workspace.isPersonal && !workspace.isGroup;
  const workspaceMembers = workspace.users;
  const restMembers = workspaceMembers.filter(({ id }) => id !== currentUser.id);
  const isNotInvited = isTeammateItem && !restMembers.length;
  const inviteTeammateSuggestion = workspace.inviteTeammateSuggestion;
  const isInvitePending = Boolean(
    isTeammateItem &&
      restMembers.length &&
      ((!restMembers[0].confirmedAt && restMembers[0].confirmationSentAt) || !restMembers[0].lastSeenAt)
  );
  const hideResendInvite = isPiUser && isInvitePending;

  const handleInviteLaterClick = () => {
    setIsShowingCalendarIntegrationBlocker(false);
  };

  const handleInviteClick = () => {
    setHasInvitedInSession(true);
    setIsShowingCalendarIntegrationBlocker(false);
  };

  const handleInviteClickIinPopover = () => {
    setHasInvitedInSession(true);
    setIsShowingCalendarIntegrationBlocker(false);
    setInviteModalParam(true);
  };

  useEffect(() => {
    setHasInvitedInSession(false);
  }, [workspaceId]);

  useEffect(() => {
    if (!hasSeenCalendarIntegration2023InviteCallout && currentUser?.flowType === 'calendar_integration_2023') {
      setIsShowingCalendarIntegrationBlocker(true);
      updateFlag({
        flag: 'has_seen_calendar_integration_2023_invite_callout',
        value: convertMomentToUnix(getCurrentMoment()),
      });
    }
  }, []);

  if (!isNotInvited && !isInvitePending && hasInvitedInSession) return null;

  const baseClassName = 'WorkspaceInviteButton';

  const pendingJsx = (
    <span className={`${baseClassName}__pending-label`}>
      <i>Invite Pending</i>
    </span>
  );

  if (hideResendInvite) return pendingJsx;

  if (isShowingCalendarIntegrationBlocker && isNotInvited) {
    return (
      <>
        <WpPopover showArrow={false} open dismisssOnOutsidePress={false} maxWidth="400px">
          <WpPopoverToggle>
            <div className={`${baseClassName}__invite`}>
              <InviteTeammateButton
                workspaceId={workspaceId}
                invitedEmail={inviteTeammateSuggestion?.email}
                teammateName={inviteTeammateSuggestion?.fullName || workspaceName}
                workspaceTitle={workspaceName}
                onClick={handleInviteClick}
                size="sm"
                icon="smile"
              />
            </div>
          </WpPopoverToggle>
          <WpPopoverContent>
            <WpCard className={`${baseClassName}__popover-card`}>
              <WpCardBody padding={0}>
                <div className={`${baseClassName}__popover-card__header`}>
                  <WpIllustration
                    src={Illustrations.three_people_in_windows_with_white_bg}
                    className={`${baseClassName}__popover-card__header__img`}
                  />
                </div>
              </WpCardBody>
              <WpCardBody>
                <div className={`${baseClassName}__popover-card__body`}>
                  <h5 className={`${baseClassName}__popover-card__body__title`}>When you&apos;re ready</h5>
                  <p>
                    1. Send out an invite.
                    <br />
                    2. Message them separately, letting them know the invitation is coming via email and what you expect
                    them to do with PI Perform.
                  </p>
                  <WpButtonGroup>
                    <WpButton block variant="secondary" onClick={handleInviteLaterClick}>
                      Invite Later
                    </WpButton>
                    <WpButton block onClick={handleInviteClickIinPopover}>
                      Invite Now
                    </WpButton>
                  </WpButtonGroup>
                </div>
              </WpCardBody>
            </WpCard>
          </WpPopoverContent>
        </WpPopover>
        <WpBackdrop onClick={() => null} />
      </>
    );
  }
  return (
    <>
      {isNotInvited && (
        <InviteTeammateButton
          workspaceId={workspaceId}
          invitedEmail={inviteTeammateSuggestion?.email}
          teammateName={inviteTeammateSuggestion?.fullName || workspaceName}
          workspaceTitle={workspaceName}
          onClick={() => setHasInvitedInSession(true)}
          size="sm"
          icon={inviteBtnIcon || 'smile'}
          btnText={inviteBtnText}
          variant={btnVariant}
        />
      )}
      {isInvitePending && !hasInvitedInSession && (
        <ResendInviteButton onClick={() => setHasInvitedInSession(true)} size="sm" variant={btnVariant} />
      )}
      {includePendingMessage && isInvitePending && hasInvitedInSession && <>{pendingJsx}</>}
    </>
  );
};

export default WorkspaceInviteButton;
